<template>
  <div class="file-badges">
    <div class="flex flex-wrap">
      <div
        v-for="(fileRelation, i) in modelValue"
        :key="fileRelation.id"
        @click.stop="onClickFileRelation(fileRelation)"
        @dragstart="onDragStart($event, fileRelation)"
        @dragend="onDragEnd"
        draggable="true"
      >
        <MultiBadge
          :size="small ? 'sm' : 'md'"
          :removable="false"
          @remove="removeFileRelation(i)"
          class="mt-1 mr-1"
          :class="selected === fileRelation ? 'bg-primary dark:bg-primary-dark ring-2' : ''"
        >
          {{ fileRelation.file.originalName }}
        </MultiBadge>
      </div>
      <LoadingSpinner :show="showUploadingState" text-class="w-4 h-4" class="mt-0.5 ml-1" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { inject } from "vue"
import { storeToRefs } from "pinia"
import MultiBadge from "@/components/misc/MultiBadge.vue"
import { FileRelation } from "@/models/file/file-relation.model"
import LoadingSpinner from "@/components/loading/LoadingSpinner.vue"
import { useDialogStore } from "@/store/dialog.store"
import { DialogTypes } from "@/interfaces/component/dialog.interface"
import { FileViewerController } from "@/controllers/file-viewer-controller"
import { useDragStore } from "@/store/drag-store"
import { FileController } from "@/controllers/file-controller"
import { FileHelper } from "@/helper/file-helper.ts"

const props = defineProps({
  /**
   * While true, shows a loading spinner to indicate a file being uploaded.
   */
  showUploadingState: {
    type: Boolean,
    default: false
  },
  /**
   * If true, shows a smaller version of the badges.
   */
  small: {
    type: Boolean,
    default: false
  },
  /**
   * If set, this function will be called when a viewable file is clicked.
   * The function receives the file relation that was clicked.
   */
  customFileViewerHandler: {
    type: Function,
    default: null
  },
  selected: {
    type: Object as () => FileRelation,
    default: null
  },
  /**
   * If true, dragging files will copy them instead of moving them.
   */
  dragDefaultIsCopy: {
    type: Boolean,
    default: false
  }
})

defineEmits(["fileRemoved", "showUploadingState"])

const modelValue = defineModel<FileRelation[]>({ required: true })

const dragStore = useDragStore()
const dialogStore = useDialogStore()
const { dragData } = storeToRefs(dragStore)
const { currentDialog } = storeToRefs(dialogStore)

const fileController: FileController = inject("fileController")!
async function removeFileRelation(index: number) {
  currentDialog.value = {
    title: "Bestand verwijderen",
    body: `Weet je zeker dat je ${modelValue.value[index].file.originalName}.${modelValue.value[index].file.extension.toLowerCase()} wilt verwijderen?`,
    type: DialogTypes.CONFIRM,
    onConfirm: () => {
      const fileRelation = modelValue.value[index]
      fileController.deleteFileRelation(fileRelation)
      modelValue.value.splice(modelValue.value.indexOf(fileRelation), 1)
    },
    textConfirm: "Verwijderen",
    classConfirm: "bg-error! text-on-error!"
  }
}

function onClickFileRelation(fileRelation: FileRelation) {
  if (FileHelper.isSupportedFile(fileRelation.file)) {
    if (props.customFileViewerHandler) {
      props.customFileViewerHandler(modelValue, fileRelation)
    } else {
      FileViewerController.display(modelValue.value, fileRelation)
    }
  } else {
    // use window open to prevent card from collapsing
    window.open(fileRelation.file.url, "_blank")
  }
}

function onDragStart(e: any, fileRelation: FileRelation) {
  e.dataTransfer.setData("fileRelationUuid", fileRelation.id)

  const isMac = navigator.userAgent.indexOf("Mac") > -1
  const isWin = navigator.userAgent.indexOf("Win") > -1
  const isCopyOperation = props.dragDefaultIsCopy || (isWin && e.ctrlKey) || (isMac && e.altKey)
  e.dataTransfer.setData("intention", isCopyOperation ? "copy" : "move")
  dragData.value = fileRelation
}

function onDragEnd() {
  dragData.value = null
}
</script>

<style scoped></style>
